import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTemplates, useDeleteTemplate, useLaunchTemplate } from '@/hooks/useTemplates';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { ApiError } from '@/api/client';
import { relTime } from '@/lib/format';
import type { TemplateConfig } from '@/types';

function streamSummary(cfg: TemplateConfig): string {
  const n = cfg.streams.length;
  const first = cfg.streams[0];
  const proto = first ? `${first.protocol}/${first.direction} ${first.bitrate_mbps || '∞'}M` : '';
  return n === 1 ? proto : `${n} streams`;
}

export function Templates() {
  const { data, isLoading, error } = useTemplates();
  const del = useDeleteTemplate();
  const launch = useLaunchTemplate();
  const nav = useNavigate();
  const [msg, setMsg] = useState<string | null>(null);

  async function runIt(id: string, name: string) {
    setMsg(null);
    try {
      const session = await launch.mutateAsync(id);
      nav(`/sessions/${session.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) setMsg('Traffic balance is empty — top up first.');
      else if (e instanceof ApiError && e.status === 504)
        setMsg(`"${name}" failed to start — are both agents online?`);
      else setMsg(e instanceof Error ? e.message : 'Launch failed.');
    }
  }

  return (
    <div className="fade-up">
      <div className="page-head">
        <div className="eyebrow">library</div>
        <div className="title">Templates</div>
        <div className="sub">
          Saved test configs. Launch one to run it now, or schedule it. Create templates from{' '}
          <a onClick={() => nav('/sessions/new')} style={{ cursor: 'pointer' }}>New test</a>.
        </div>
      </div>

      {msg && <div className="panel" style={{ borderColor: 'var(--bad)', marginBottom: 12 }}>{msg}</div>}

      {isLoading ? (
        <Loading label="Loading templates" />
      ) : error ? (
        <ErrorBanner error={error} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          title="No templates yet"
          hint='Configure a test under "New test" and click "Save as template".'
        />
      ) : (
        <div className="panel">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Traffic</th>
                <th>Firmware</th>
                <th>Tag</th>
                <th>Updated</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="muted">{streamSummary(t.config)}</td>
                  <td className="muted">{t.config.firmware || '—'}</td>
                  <td className="muted">{t.config.tag || '—'}</td>
                  <td className="muted">{relTime(t.updated_at)}</td>
                  <td className="right">
                    <button
                      className="btn btn-primary"
                      onClick={() => runIt(t.id, t.name)}
                      disabled={launch.isPending}
                    >
                      Launch
                    </button>
                    <button
                      className="btn"
                      style={{ marginLeft: 8 }}
                      onClick={() => {
                        if (confirm(`Delete template "${t.name}"?`)) del.mutate(t.id);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
